import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, useMediaQuery, Typography } from '@mui/material';
import GeneralTile from './GeneralTile';
import MyBoards from './MyBoards';
import eventBus, { eventBusValues } from '../../eventBus';
import my_messages from '../../icons/my-messages.svg';
import my_professional_brand from '../../icons/my-professional-brand.svg';
import calendar from '../../icons/calendar.svg';
import professional_goals from '../../icons/professional_goals.svg';
import { useAccount } from '../../contexts/Account';
import LeadershipFrame from '../../components/LeadershipFrame';
import InvitationsBoard from '../../components/Advisor/InvitationsBoard';
import { getSavedCompanies } from '../../services/advisor_services';
import { advisorSuggestedOpportunitiesSubscription } from '../../services/subscriptions';

import ac_profile from '../../icons/my-ac-profile.svg';
import goals_icon from '../../icons/my-professional-goals.svg';
import social_boost from '../../icons/social-boost.svg';
import become_feature from '../../icons/become-feature.svg';
import Treatment from '../../components/Treatment';
import { featureFlags } from '../../splitSetup';
import ProfessionalBrandSection from './ProfessionalBrandSection';

function BoardsAndInvitations() {
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const isLargeScreen = useMediaQuery('(min-width:1150px)');
  const { accountDetails, currentUserInfo, isFreemium, conversations } =
    useAccount();
  const [showDetails, setShowDetails] = useState(false);
  const [showRecomendations, setShowRecomendations] = useState(false);
  const [showLeadershipFrame, setShowLeadershipFrame] = useState(false);
  const [showProfessionalGoals, setShowProfessionalGoals] = useState(false);
  const [showAdvisoryBoards, setShowAdvisoryBoards] = useState(false);
  const [showProfessionalBrand, setShowProfessionalBrand] = useState(false);

  const history = useHistory();

  const detailsRef = useRef(null);
  const mobileGoalsRef = useRef(null);
  const mobileBoardsRef = useRef(null);
  const recommendationsRef = useRef(null);
  const profBrandRef = useRef(null);

  const totalInvitations = accountDetails.boards.invitedBoards.length;
  const [savedCompanies, setSavedCompanies] = useState([]);
  const totalSaved = savedCompanies.length || 0;

  const loadSavedCompanies = useCallback(() => {
    if (!accountDetails?.id) return;
    getSavedCompanies({ advisorId: accountDetails.id })
      .then((response) => {
        const companies = response.data.getSavedCompanies.filter(
          (c) =>
            c.boardOwnerStatus !== 'inactive' &&
            c.boardOwnerStatus !== 'paused' &&
            c.opportunityStage !== 'dormant' &&
            c.opportunityStage !== 'pending_cancellation' &&
            c.opportunityStage !== 'position_filled'
        );
        setSavedCompanies(companies);
      })
      .catch((error) =>
        console.error('Error fetching saved companies:', error)
      );
  }, [accountDetails?.id]);

  useEffect(() => {
    if (showDetails) {
      setTimeout(() => {
        if (isXS && showAdvisoryBoards) {
          mobileBoardsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        } else if (isXS && showProfessionalGoals) {
          mobileGoalsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        } else if (!isXS) {
          detailsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 100);
    }
  }, [showDetails, isXS, showAdvisoryBoards, showProfessionalGoals]);

  useEffect(() => {
    if (showRecomendations) {
      setTimeout(() => {
        recommendationsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [showRecomendations]);

  useEffect(() => {
    if (showProfessionalBrand) {
      setTimeout(() => {
        profBrandRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [showProfessionalBrand]);

  useEffect(() => {
    loadSavedCompanies();
  }, [loadSavedCompanies]);

  useEffect(() => {
    if (!accountDetails?.id) return undefined;
    const subscription = advisorSuggestedOpportunitiesSubscription(
      { advisorId: accountDetails.id },
      loadSavedCompanies
    );
    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [accountDetails?.id, loadSavedCompanies]);

  const sections = {
    advisoryBoards: setShowAdvisoryBoards,
    recommendations: setShowRecomendations,
    professionalBrand: setShowProfessionalBrand,
  };

  function resetAllSections() {
    Object.values(sections).forEach((setSection) => setSection(false));
    setShowDetails(false);
  }

  function toggleSection(sectionKey) {
    const isOpen =
      sectionKey === 'advisoryBoards'
        ? showAdvisoryBoards
        : sectionKey === 'recommendations'
        ? showRecomendations
        : showProfessionalBrand;

    resetAllSections();
    sections[sectionKey](!isOpen);
    if (sectionKey !== 'professionalBrand') {
      setShowDetails(!isOpen);
    }
  }

  function getUnreadMessages() {
    let totalUnread = 0;
    conversations.forEach((conv) => {
      totalUnread += conv.unreadCount;
    });
    return totalUnread + (accountDetails.readCeoMessage ? 0 : 1);
  }

  const openProfileDrawer = () => {
    eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
      ...accountDetails,
      // openInEdit: false,
    });
  };

  const openProfessionalGoalsDrawer = () => {
    eventBus.dispatch('openProfessionalGoals');
  };

  const goToSocialBoost = () => {
    history.push('/ub/advisor/services/social');
  };

  return (
    <>
      <LeadershipFrame
        showLeadershipFrame={showLeadershipFrame}
        setShowLeadershipFrame={setShowLeadershipFrame}
      />
      <Box
        sx={
          isXS || isLargeScreen
            ? {
                display: 'flex',
                flexDirection: isXS ? 'column' : 'row',
                justifyContent: 'space-between',
                columnGap: '20px',
                rowGap: '20px',
              }
            : {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '20px',
              }
        }
      >
        <GeneralTile
          ids={{
            tileID: 'my-advisory-boards-tile-id',
            buttonID: 'my-advisory-boards-button-id',
          }}
          title="My Advisory Boards"
          onClick={() => toggleSection('advisoryBoards')}
          description="View your current advisory boards, share your ideas, make an introduction, attend board meetings and more."
          image={calendar}
        />
        {showDetails && showAdvisoryBoards && isXS ? (
          <Box ref={mobileBoardsRef} sx={{ scrollMarginTop: '130px' }}>
            <MyBoards />
          </Box>
        ) : null}

        <GeneralTile
          ids={{
            tileID: 'my-professional-brand-tile-id',
            buttonID: 'my-professional-brand-button-id',
          }}
          title="My Professional Brand"
          onClick={() => toggleSection('professionalBrand')}
          description="View or update your AdvisoryCloud profile to attract senior-level opportunities and valuable networking connections."
          image={my_professional_brand}
        />

        {showProfessionalBrand && isXS && (
          <ProfessionalBrandSection
            ref={profBrandRef}
            isXS={isXS}
            isLargeScreen={isLargeScreen}
            accountDetails={accountDetails}
            openProfileDrawer={openProfileDrawer}
            openProfessionalGoalsDrawer={openProfessionalGoalsDrawer}
            goToSocialBoost={goToSocialBoost}
          />
        )}
        <GeneralTile
          ids={{
            tileID: 'my-recommendations-tile-id',
            buttonID: 'my-recommendations-button-id',
          }}
          title="My Recommendations"
          onClick={() => toggleSection('recommendations')}
          description="View top matches and recommendations related to board roles and other senior-level opportunities on AdvisoryCloud."
          image={professional_goals}
          badge={totalInvitations + totalSaved}
          buttonText="View"
        />
        {showRecomendations && isXS && (
          <Box
            ref={recommendationsRef}
            sx={{ display: 'block', marginTop: '20px' }}
          >
            <InvitationsBoard />
          </Box>
        )}
        <GeneralTile
          ids={{
            tileID: 'my-messages-tile-id',
            buttonID: 'my-messages-button-id',
          }}
          title="My Messages"
          onClick={() =>
            eventBus.dispatch(eventBusValues.triggerConversationDrawerV2, {})
          }
          description="View messages from your boards and fellow advisors on AdvisoryCloud. Check back every month to see your new opportunities."
          image={my_messages}
          badge={getUnreadMessages()}
        />
      </Box>

      {showDetails && !isXS && (
        <Box ref={detailsRef} sx={{ marginTop: '20px' }}>
          {showAdvisoryBoards && <MyBoards />}
        </Box>
      )}
      {showRecomendations && !isXS && (
        <Box
          ref={recommendationsRef}
          sx={{
            display: 'block',
            marginTop: '20px',
          }}
        >
          <InvitationsBoard />
        </Box>
      )}

      {showProfessionalBrand && !isXS && (
        <ProfessionalBrandSection
          ref={profBrandRef}
          isXS={isXS}
          isLargeScreen={isLargeScreen}
          accountDetails={accountDetails}
          openProfileDrawer={openProfileDrawer}
          openProfessionalGoalsDrawer={openProfessionalGoalsDrawer}
          goToSocialBoost={goToSocialBoost}
        />
      )}
    </>
  );
}

export default BoardsAndInvitations;
